import React, { useState, useEffect, useMemo } from 'react';
import { 
    Select, Table, Typography, Tag, Space, Card, Row, Col, 
    DatePicker, Button, Divider, message, Tooltip, Modal, Input, Popconfirm, Checkbox,
    Spin, List, Empty,
    Collapse, Badge
} from 'antd';
import { 
    ReloadOutlined, ScheduleOutlined, DownloadOutlined, 
    BarChartOutlined, EditOutlined, DeleteOutlined,
    EnvironmentOutlined, MedicineBoxOutlined, StarFilled,
    CheckCircleFilled, AuditOutlined, UserOutlined, RocketOutlined,
    CrownOutlined,
    WarningOutlined, CheckCircleOutlined 
} from '@ant-design/icons';
import { getAllDelegatesList } from '../../services/delegateService';
import { getWeeklyMissedVisits } from '../../services/analyticsService';
import axiosInstance from '../../services/axiosServicesVisit'; 
import dayjs from 'dayjs';
import Papa from 'papaparse';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

const WeeklyMissedVisitsPage = () => {
    const [delegates, setDelegates] = useState([]);
    const [selectedDelegateId, setSelectedDelegateId] = useState(null);
    const [dateRange, setDateRange] = useState([dayjs('2026-01-01'), dayjs('2026-12-31')]);
    const [visitStatusFilter, setVisitStatusFilter] = useState('all');
    const [selectedCycleIds, setSelectedCycleIds] = useState([]); 
    const [selectedWeek, setSelectedWeek] = useState('all'); 
    const [loading, setLoading] = useState(false);
    const [rawApiData, setRawApiData] = useState([]); 
    const [analysisData, setAnalysisData] = useState({ tableData: [], cycleOptions: [] });

    // États édition justification
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [editingComment, setEditingComment] = useState({ id: null, doctor: '', text: '', week: '' });
    const [actionLoading, setActionLoading] = useState(false);

    // Filtre médecin
    const [selectedPhysicianId, setSelectedPhysicianId] = useState(null);

    // États modale Parcours de visite du Médecin
    const [doctorModalVisible, setDoctorModalVisible] = useState(false);
    const [doctorDetailData, setDoctorDetailData] = useState([]);
    const [doctorLoading, setDoctorLoading] = useState(false);

    useEffect(() => {
        getAllDelegatesList().then(res => setDelegates(res.data || []));
    }, []);

    const fetchAnalysis = () => {
        if (!selectedDelegateId) return;
        setLoading(true);
        const start = dateRange ? dateRange[0].format('YYYY-MM-DD') : undefined;
        const end = dateRange ? dateRange[1].format('YYYY-MM-DD') : undefined;

        getWeeklyMissedVisits(selectedDelegateId, start, end)
            .then(response => {
                setRawApiData(response.data || []); 
                setAnalysisData(formatDataForTable(response.data || []));
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchAnalysis(); }, [selectedDelegateId, dateRange]);

    const toggleCycleSelection = (cycleId) => {
        setSelectedCycleIds(prev => 
            prev.includes(cycleId) 
                ? prev.filter(id => id !== cycleId) 
                : [...prev, cycleId]
        );
    };

    // --- OUVERTURE DE L'HISTORIQUE / PARCOURS DU MÉDECIN ---
    const handleOpenDoctorHistory = async (personId) => {
        if (!personId) return;
        setDoctorModalVisible(true);
        setDoctorLoading(true);
        try {
            const res = await axiosInstance.get(`/v1/delegate-stocks/analytics/doctor-full-details`, {
                params: { personId, startDate: '2020-01-01', endDate: dayjs().format('YYYY-MM-DD'), _t: Date.now() }
            });
            setDoctorDetailData(res.data || []);
        } catch (e) {
            message.error("Erreur lors de la récupération de l'historique");
        } finally {
            setDoctorLoading(false);
        }
    };

    // ⚡ EXPORT CSV DU PARCOURS AVEC LES NOTES / OBSERVATIONS ⚡
    const handleExportDoctorHistory = () => {
        if (doctorDetailData.length === 0) return;
        const docName = doctorDetailData[0].doctorName;
        const headers = ["Date", "Region", "Delegue Visiteur", "Type Visite", "Echantillons", "Produits", "Observations / Note"];
        const rows = doctorDetailData.map(item => {
            const isSupervisor = item.isSupervisor || 
                (item.note && (item.note.includes('[RAPPORT SUPERVISEUR') || item.note.includes('[VISITE DE CONTRÔLE') || item.note.includes('[SUPERVISION'))) ||
                (item.name && item.name.includes('[SUPERVISEUR]'));

            return [
                dayjs(item.reportDate).format('DD/MM/YYYY HH:mm'),
                item.regionName || "N/A",
                item.delegateName,
                isSupervisor ? "Superviseur" : "Délégué",
                item.samples?.map(s => `${s.name} (x${s.quantity})`).join(' | '),
                item.products?.join(' | '),
                `"${(item.note || item.comment || '').replace(/"/g, '""')}"`
            ];
        });
        const csvContent = [headers, ...rows].map(e => e.join(";")).join("\n");
        const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Parcours_${docName.replace(/\s+/g, '_')}.csv`;
        link.click();
    };

    const handleOpenEdit = (doctorName, reasonObj, weekNum) => {
        setEditingComment({ id: reasonObj.id, doctor: doctorName, text: reasonObj.comment, week: weekNum });
        setIsEditModalVisible(true);
    };

    const handleUpdateComment = async () => {
        if (!editingComment.text) return message.warning("Le texte ne peut pas être vide.");
        setActionLoading(true);
        try {
            await axiosInstance.patch(`/v1/analytics/missed-visit-justification/${editingComment.id}`, { comment: editingComment.text });
            message.success("Justification mise à jour.");
            setIsEditModalVisible(false);
            fetchAnalysis(); 
        } catch (e) { message.error("Erreur lors de la mise à jour"); }
        finally { setActionLoading(false); }
    };

    const handleDeleteComment = async (id) => {
        try {
            await axiosInstance.delete(`/v1/analytics/missed-visit-justification/${id}`);
            message.success("Justification supprimée.");
            fetchAnalysis();
        } catch (e) { message.error("Erreur lors de la suppression"); }
    };

    const formatDataForTable = (apiData) => {
        const physicianMap = new Map();
        const cycleOptions = apiData.map(c => ({ value: c.cycleId, label: c.cycleName }));

        apiData.forEach(cycle => {
            cycle.planning.forEach(plan => {
                if (!physicianMap.has(plan.person.id)) {
                    physicianMap.set(plan.person.id, { 
                        key: plan.person.id, 
                        physicianName: `${plan.person.firstname} ${plan.person.lastname}`,
                        specialty: plan.person.specialtyName,
                        region: plan.person.regionName,
                        isPartner: plan.person.isPartner,
                        partnerNotes: plan.person.partnerNotes
                    });
                }
                const row = physicianMap.get(plan.person.id);
                row[`cycle_${cycle.cycleId}`] = { 
                    cycleName: cycle.cycleName, 
                    missed: plan.missedInWeeks || [], 
                    visited: plan.visitedInWeeks || [], 
                    recovered: plan.recoveredInfo || [], 
                    justificationsByWeek: plan.justificationsByWeek 
                };
            });
        });

        return { tableData: Array.from(physicianMap.values()), cycleOptions };
    };

    // Statistiques visites par cycle (visités / total prévus)
    const cycleStatsMap = useMemo(() => {
        const stats = {};
        const weekNum = selectedWeek !== 'all' ? parseInt(selectedWeek, 10) : null;

        rawApiData.forEach(cycle => {
            let totalVisited = 0;
            let totalPlanned = 0;

            (cycle.planning || []).forEach(plan => {
                if (weekNum) {
                    const isVisited = plan.visitedInWeeks?.includes(weekNum) || plan.recoveredInfo?.some(r => r.missed === weekNum);
                    const isMissed = plan.missedInWeeks?.includes(weekNum);
                    if (isVisited) {
                        totalVisited++;
                        totalPlanned++;
                    } else if (isMissed) {
                        totalPlanned++;
                    }
                } else {
                    const vCount = (plan.visitedInWeeks?.length || 0) + (plan.recoveredInfo?.length || 0);
                    const mCount = plan.missedInWeeks?.length || 0;
                    totalVisited += vCount;
                    totalPlanned += (vCount + mCount);
                }
            });

            const percent = totalPlanned > 0 ? Math.round((totalVisited / totalPlanned) * 100) : 0;
            stats[cycle.cycleId] = { totalVisited, totalPlanned, percent };
        });

        return stats;
    }, [rawApiData, selectedWeek]);

    // Colonnes dynamiques avec compteurs dans le titre
    const dynamicCycleColumns = useMemo(() => {
        const weekNum = selectedWeek !== 'all' ? parseInt(selectedWeek, 10) : null;

        return rawApiData.map(cycle => {
            const stats = cycleStatsMap[cycle.cycleId] || { totalVisited: 0, totalPlanned: 0, percent: 0 };
            const isCompleted = stats.totalPlanned > 0 && stats.totalVisited === stats.totalPlanned;

            return {
                title: (
                    <div style={{ textAlign: 'center' }}>
                        <Text strong style={{ color: '#003a8c', display: 'block', fontSize: '13px' }}>
                            {cycle.cycleName}
                        </Text>
                        <div style={{ marginTop: '4px' }}>
                            <Tag 
                                color={isCompleted ? 'success' : stats.totalVisited > 0 ? 'blue' : 'default'} 
                                style={{ 
                                    fontWeight: 'bold', 
                                    margin: 0, 
                                    padding: '1px 8px', 
                                    borderRadius: '10px',
                                    fontSize: '11px',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                }}
                            >
                                {stats.totalVisited} / {stats.totalPlanned} ({stats.percent}%)
                            </Tag>
                        </div>
                    </div>
                ),
                dataIndex: `cycle_${cycle.cycleId}`,
                key: `cycle_${cycle.cycleId}`,
                width: 150,
                align: 'center',
                render: (info, record) => {
                    if (!info) return <Text type="secondary">-</Text>;
                    
                    return (
                        <Space direction="vertical" size={3} style={{ width: '100%' }}>
                            {info.visited?.filter(w => !weekNum || w === weekNum).map(w => (
                                <Tag color="green" key={`v_${w}`} style={{ width: '100%', textAlign: 'center', margin: 0 }}>
                                    S{w} Visité
                                </Tag>
                            ))}

                            {info.recovered?.filter(r => !weekNum || r.missed === weekNum).map((r, idx) => (
                                <Tooltip key={`rec_${idx}`} title={`Prévu en S${r.missed}, mais visité en S${r.recoveredBy}`}>
                                    <Tag color="cyan" style={{ width: '100%', textAlign: 'center', margin: 0, fontWeight: 'bold' }}>
                                        S{r.missed} Rattrapé (en S{r.recoveredBy})
                                    </Tag>
                                </Tooltip>
                            ))}

                            {info.missed?.filter(w => !weekNum || w === weekNum).map(w => (
                                <div key={`m_${w}`} style={{ width: '100%' }}>
                                    <Tag color="error" style={{ width: '100%', textAlign: 'center', margin: 0 }}>
                                        S{w} Manqué
                                    </Tag>
                                    {info.justificationsByWeek && info.justificationsByWeek[w] && (
                                        <div style={{ marginTop: 4, padding: '4px', background: '#fff1f0', border: '1px dashed #ffa39e', borderRadius: '4px' }}>
                                            <div style={{ fontSize: '11px', fontStyle: 'italic', color: '#cf1322', marginBottom: 4, textAlign: 'center' }}>
                                                "{info.justificationsByWeek[w].comment}"
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                                                <EditOutlined style={{ color: '#1890ff', cursor: 'pointer' }} onClick={() => handleOpenEdit(record.physicianName, info.justificationsByWeek[w], w)} />
                                                <Popconfirm title="Supprimer cette raison ?" onConfirm={() => handleDeleteComment(info.justificationsByWeek[w].id)}>
                                                    <DeleteOutlined style={{ color: '#ff4d4f', cursor: 'pointer' }} />
                                                </Popconfirm>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </Space>
                    );
                }
            };
        });
    }, [rawApiData, selectedWeek, cycleStatsMap]);

    const physicianOptions = useMemo(() => {
        return analysisData.tableData.map(row => ({
            value: row.key,
            label: `${row.isPartner ? '⭐ ' : ''}${row.physicianName}`,
            rawName: row.physicianName
        })).sort((a, b) => a.rawName.localeCompare(b.rawName));
    }, [analysisData.tableData]);

    const finalColumns = useMemo(() => {
        const base = [{ 
            title: 'Médecin', dataIndex: 'physicianName', key: 'physicianName', fixed: 'left', width: 230, 
            render: (text, record) => (
                <div>
                    <Space>
                        {record.isPartner && <StarFilled style={{ color: '#fadb14' }} />}
                        <a 
                            onClick={() => handleOpenDoctorHistory(record.key)}
                            style={{ 
                                fontWeight: 'bold', 
                                color: record.isPartner ? '#856404' : '#1890ff', 
                                fontSize: '13px',
                                textDecoration: 'underline'
                            }}
                        >
                            {text}
                        </a>
                    </Space>
                    <div style={{ marginTop: '2px', display: 'flex', flexDirection: 'column' }}>
                        <Text type="secondary" style={{ fontSize: '10px' }}><MedicineBoxOutlined /> {record.specialty}</Text>
                        <Text type="secondary" style={{ fontSize: '10px' }}><EnvironmentOutlined /> {record.region}</Text>
                    </div>
                </div>
            )
        }];
        let dynamic = dynamicCycleColumns;
        if (selectedCycleIds.length > 0) {
            const selectedIdsNumbers = selectedCycleIds.map(Number);
            dynamic = dynamic.filter(col => selectedIdsNumbers.includes(Number(col.key.replace('cycle_', ''))));
        }
        return [...base, ...dynamic];
    }, [dynamicCycleColumns, selectedCycleIds]);

    const filteredTableData = useMemo(() => {
        return analysisData.tableData.filter(row => {
            if (selectedPhysicianId && row.key !== selectedPhysicianId) {
                return false;
            }

            const cycleKeys = Object.keys(row).filter(k => k.startsWith('cycle_'));
            const activeKeys = selectedCycleIds.length > 0 ? cycleKeys.filter(k => selectedCycleIds.includes(parseInt(k.replace('cycle_', '')))) : cycleKeys;
            if (activeKeys.length === 0 && selectedCycleIds.length > 0) return false;
            
            return activeKeys.some(k => {
                const cell = row[k]; if (!cell) return false;
                const weekNum = selectedWeek !== 'all' ? parseInt(selectedWeek) : null;
                if (weekNum && !cell.visited.includes(weekNum) && !cell.missed.includes(weekNum)) return false;
                if (visitStatusFilter === 'all') return true;
                if (visitStatusFilter === 'visited') return weekNum ? cell.visited.includes(weekNum) : cell.visited.length > 0;
                if (visitStatusFilter === 'nonVisited') return weekNum ? cell.missed.includes(weekNum) : cell.missed.length > 0;
                return true;
            });
        });
    }, [analysisData.tableData, visitStatusFilter, selectedWeek, selectedCycleIds, selectedPhysicianId]);

    // Export CSV
    const handleExportCsv = () => {
        if (!filteredTableData || filteredTableData.length === 0) {
            return message.warning("Aucune donnée à exporter.");
        }

        const visibleCycles = rawApiData.filter(cycle => {
            if (selectedCycleIds.length === 0) return true;
            return selectedCycleIds.includes(cycle.cycleId);
        });

        const weekNum = selectedWeek !== 'all' ? parseInt(selectedWeek, 10) : null;

        const exportRows = filteredTableData.map(row => {
            const item = {
                'Médecin': row.physicianName,
                'Partenaire': row.isPartner ? 'Oui' : 'Non',
                'Spécialité': row.specialty || '',
                'Région': row.region || '',
            };

            visibleCycles.forEach(cycle => {
                const cell = row[`cycle_${cycle.cycleId}`];
                const stats = cycleStatsMap[cycle.cycleId];
                const cycleHeaderName = `${cycle.cycleName} (${stats ? `${stats.totalVisited}/${stats.totalPlanned}` : ''})`;

                if (!cell) {
                    item[cycleHeaderName] = '-';
                    return;
                }

                const details = [];

                cell.visited?.filter(w => !weekNum || w === weekNum).forEach(w => {
                    details.push(`S${w} Visité`);
                });

                cell.recovered?.filter(r => !weekNum || r.missed === weekNum).forEach(r => {
                    details.push(`S${r.missed} Rattrapé (en S${r.recoveredBy})`);
                });

                cell.missed?.filter(w => !weekNum || w === weekNum).forEach(w => {
                    const justif = cell.justificationsByWeek && cell.justificationsByWeek[w]?.comment 
                        ? ` [Raison: ${cell.justificationsByWeek[w].comment}]` 
                        : '';
                    details.push(`S${w} Manqué${justif}`);
                });

                item[cycleHeaderName] = details.length > 0 ? details.join(' | ') : '-';
            });

            return item;
        });

        const csvString = Papa.unparse(exportRows, { delimiter: ';' });
        const blob = new Blob(['\uFEFF' + csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        const selectedDelegate = delegates.find(d => d.id === selectedDelegateId);
        const delegateName = selectedDelegate ? `${selectedDelegate.firstname}_${selectedDelegate.lastname}` : 'delegue';
        const dateStr = dayjs().format('YYYY-MM-DD');

        link.setAttribute('href', url);
        link.setAttribute('download', `analyse_visites_${delegateName}_${dateStr}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        message.success("Export CSV téléchargé avec succès.");
    };

    return (
        <div style={{ padding: '20px' }}>
            <Card title={<Space><BarChartOutlined /> <Text strong style={{fontSize: '20px'}}>Analyse Hebdomadaire des Visites</Text></Space>} bordered={false}>
                <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 20 }}>
                    <Col>
                        <Select 
                            showSearch 
                            placeholder="Délégué" 
                            style={{ width: 180 }} 
                            onChange={v => { setSelectedDelegateId(v); setSelectedCycleIds([]); setSelectedPhysicianId(null); }} 
                            optionFilterProp="label"
                            options={delegates.map(d => ({ value: d.id, label: `${d.firstname} ${d.lastname}` }))} 
                        />
                    </Col>
                    <Col><RangePicker value={dateRange} onChange={setDateRange} format="DD/MM/YYYY" style={{ width: 230 }} /></Col>
                    <Col>
                        <Select 
                            showSearch 
                            allowClear
                            placeholder="Filtrer Médecin" 
                            style={{ width: 210 }} 
                            value={selectedPhysicianId} 
                            onChange={setSelectedPhysicianId}
                            disabled={!selectedDelegateId || analysisData.tableData.length === 0}
                            optionFilterProp="label"
                            filterOption={(input, option) => 
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={physicianOptions}
                        />
                    </Col>
                    <Col>
                        <Select 
                            mode="multiple" 
                            placeholder="Filtrer Cycles" 
                            style={{ width: 220 }} 
                            value={selectedCycleIds} 
                            onChange={setSelectedCycleIds} 
                            maxTagCount="responsive"
                            optionLabelProp="label"
                        >
                            {analysisData.cycleOptions.map(opt => (
                                <Select.Option key={opt.value} value={opt.value} label={opt.label}>
                                    <Checkbox checked={selectedCycleIds.includes(opt.value)} /> {opt.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col><Select value={selectedWeek} onChange={setSelectedWeek} style={{ width: 140 }} options={[{value:'all', label:'Toutes Semaines'},{value:'1', label:'Semaine 1'},{value:'2', label:'Semaine 2'},{value:'3', label:'Semaine 3'},{value:'4', label:'Semaine 4'}]} /></Col>
                    <Col><Select value={visitStatusFilter} onChange={setVisitStatusFilter} style={{ width: 130 }} options={[{value:'all', label:'Tous Statuts'},{value:'visited', label:'Visités'},{value:'nonVisited', label:'Manqués'}]} /></Col>
                    
                    <Col>
                        <Space>
                            <Button type="primary" icon={<ReloadOutlined />} onClick={fetchAnalysis}>Actualiser</Button>
                            <Button 
                                icon={<DownloadOutlined />} 
                                onClick={handleExportCsv} 
                                disabled={!selectedDelegateId || filteredTableData.length === 0}
                            >
                                Exporter CSV
                            </Button>
                        </Space>
                    </Col>
                </Row>

                {/* ZONE DES BADGES (CALENDRIER DES CYCLES) */}
                {selectedDelegateId && rawApiData.length > 0 && (
                    <div style={{ padding: '8px 12px', background: '#f0f5ff', borderRadius: '6px', border: '1px solid #adc6ff', marginBottom: 15 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <Text strong style={{ color: '#002766', fontSize: '13px', opacity: 0.7 }}><ScheduleOutlined /> CLIQUEZ SUR UN CYCLE POUR FILTRER :</Text>
                            <Button size="small" type="link" onClick={() => setSelectedCycleIds([])}>Réinitialiser filtres</Button>
                        </div>
                        <Row gutter={[6, 6]}>
                            {[...rawApiData].sort((a, b) => a.cycleId - b.cycleId).map(cycle => {
                                const isSelected = selectedCycleIds.length === 0 || selectedCycleIds.includes(cycle.cycleId);
                                const stats = cycleStatsMap[cycle.cycleId] || { totalVisited: 0, totalPlanned: 0 };

                                return (
                                    <Col key={cycle.cycleId} xs={24} sm={12} lg={8}>
                                        <Card 
                                            hoverable
                                            size="small" 
                                            onClick={() => toggleCycleSelection(cycle.cycleId)}
                                            style={{ 
                                                borderRadius: '4px', 
                                                border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9', 
                                                background: isSelected ? '#fff' : '#fafafa',
                                                opacity: isSelected ? 1 : 0.6,
                                                position: 'relative'
                                            }} 
                                            bodyStyle={{ padding: '4px 8px' }}
                                        >
                                            {selectedCycleIds.includes(cycle.cycleId) && (
                                                <CheckCircleFilled style={{ position: 'absolute', top: -5, right: -5, color: '#1890ff', fontSize: '16px', background: '#fff', borderRadius: '50%' }} />
                                            )}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                                                <Text strong style={{ fontSize: '11px', color: isSelected ? '#1890ff' : '#8c8c8c' }}>{cycle.cycleName.toUpperCase()}</Text>
                                                <Tag color="blue" style={{ fontSize: '10px', padding: '0 4px', margin: 0, lineHeight: '18px' }}>
                                                    {stats.totalVisited}/{stats.totalPlanned}
                                                </Tag>
                                            </div>
                                            <div style={{ display: 'flex', gap: '3px' }}>
                                                {cycle.weeks?.map(w => (
                                                    <div key={w.num} style={{ flex: 1, background: isSelected ? '#f5f5f5' : '#f0f0f0', borderRadius: '3px', padding: '2px 0', textAlign: 'center', border: '1px solid #e8e8e8' }}>
                                                        <div style={{ fontSize: '10px', color: '#8c8c8c' }}>S{w.num}</div>
                                                        <div style={{ fontSize: '11px', fontWeight: 'bold' }}>{dayjs(w.start).format('DD/MM')}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    </div>
                )}

                <Table 
                    loading={loading} 
                    dataSource={filteredTableData} 
                    columns={finalColumns} 
                    bordered 
                    size="small" 
                    rowClassName={(record) => record.isPartner ? 'row-partner-gold' : ''} 
                    scroll={{ x: 'max-content', y: 550 }} 
                    pagination={{ pageSize: 20 }} 
                />

                {/* MODALE ÉDITION JUSTIFICATION */}
                <Modal 
                    title="Modifier la justification" 
                    open={isEditModalVisible} 
                    onCancel={() => setIsEditModalVisible(false)} 
                    onOk={handleUpdateComment} 
                    confirmLoading={actionLoading} 
                    okText="Mettre à jour" 
                    cancelText="Annuler"
                >
                    <div style={{ padding: '10px 0' }}>
                        <p><Text strong>Médecin :</Text> {editingComment.doctor}</p>
                        <Divider style={{ margin: '12px 0' }} />
                        <Input.TextArea rows={4} value={editingComment.text} onChange={(e) => setEditingComment({...editingComment, text: e.target.value})} />
                    </div>
                </Modal>

                {/* ========================================================================= */}
                {/* ⚡ MODALE PARCOURS DE VISITE DU MÉDECIN AVEC DISTINCTION SUPERVISEUR ⚡ */}
                {/* ========================================================================= */}
                <Modal 
                    title={
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '95%', alignItems: 'center' }}>
                            <span><AuditOutlined /> Parcours de visite du Médecin</span>
                            <Button 
                                icon={<DownloadOutlined />} 
                                size="small" 
                                type="primary" 
                                onClick={handleExportDoctorHistory} 
                                disabled={doctorDetailData.length === 0}
                            >
                                Exporter Historique
                            </Button>
                        </div>
                    } 
                    open={doctorModalVisible} 
                    onCancel={() => setDoctorModalVisible(false)} 
                    footer={null} 
                    width={1150}
                >
                    <Spin spinning={doctorLoading}>
                        {/* 1. EN-TÊTE : FICHE RÉCAPITULATIVE DU MÉDECIN */}
                        {doctorDetailData.length > 0 && (
                            (() => {
                                const firstRecord = doctorDetailData[0]; 
                                const isPartner = firstRecord.isPartner === true || firstRecord.isPartner === 1 || firstRecord.isPartner === 'true';
                                const notes = firstRecord.partnerNotes; 

                                // ⚡ 1. FILTRE : Uniquement les cycles oubliés à partir de l'année 2026 ⚡
                                const cyclesCoverage = (firstRecord.cyclesPlanningCoverage || []).map(cov => ({
                                    ...cov,
                                    unassignedCycles: (cov.unassignedCycles || []).filter(c => {
                                        if (!c.startDate) return false;
                                        return dayjs(c.startDate).year() >= 2026;
                                    })
                                }));

                                const delegatesWithUnassigned = cyclesCoverage.filter(cov => (cov.unassignedCycles || []).length > 0);
                                const totalOublisCount = delegatesWithUnassigned.reduce((acc, curr) => acc + (curr.unassignedCycles?.length || 0), 0);

                                return (
                                    <div>
                                        <div style={{ 
                                            marginBottom: 16, 
                                            padding: '16px 20px', 
                                            borderRadius: '10px', 
                                            backgroundColor: isPartner ? '#fffbe6' : '#f0f5ff', 
                                            border: isPartner ? '2px solid #fadb14' : '1px solid #adc6ff',
                                            boxShadow: isPartner ? '0 4px 12px rgba(250, 219, 20, 0.15)' : 'none'
                                        }}>
                                            <Row gutter={[16, 16]} align="middle">
                                                <Col span={16}>
                                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
                                                        <UserOutlined style={{ fontSize: '28px', marginTop: '5px', color: isPartner ? '#856404' : '#1890ff' }} />
                                                        <div>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                                                <Title level={3} style={{ margin: 0, color: isPartner ? '#856404' : '#002766' }}>
                                                                    {firstRecord.doctorName}
                                                                </Title>
                                                                {isPartner && (
                                                                    <Tag color="gold" style={{ 
                                                                        fontSize: '13px', 
                                                                        fontWeight: '900', 
                                                                        border: '1px solid #d4b106', 
                                                                        borderRadius: '4px',
                                                                        padding: '2px 10px',
                                                                        textTransform: 'uppercase'
                                                                    }}>
                                                                        <StarFilled style={{ marginRight: '6px' }} /> MÉDECIN PARTENAIRE
                                                                    </Tag>
                                                                )}
                                                            </div>

                                                            <Space wrap size={8} style={{ marginTop: '8px' }}>
                                                                <Tag color="blue" style={{ fontWeight: 'bold' }}>{firstRecord.specialtyName}</Tag>
                                                                <Text type="secondary"><EnvironmentOutlined /> {firstRecord.regionName || "N/A"}</Text>
                                                                {firstRecord.address && <Text type="secondary">({firstRecord.address})</Text>}
                                                            </Space>
                                                            
                                                            {isPartner && notes && (
                                                                <div style={{ 
                                                                    marginTop: '15px', 
                                                                    padding: '10px 15px', 
                                                                    background: 'rgba(255, 255, 255, 0.6)', 
                                                                    borderRadius: '6px', 
                                                                    borderLeft: '4px solid #fadb14' 
                                                                }}>
                                                                    <Text strong style={{ color: '#856404', display: 'block', marginBottom: '3px' }}>
                                                                        Historique Partenariat :
                                                                    </Text>
                                                                    <Text style={{ color: '#856404', fontSize: '13px' }}>
                                                                        {notes}
                                                                    </Text>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </Col>

                                                <Col span={8} style={{ 
                                                    textAlign: 'right', 
                                                    borderLeft: `2px solid ${isPartner ? '#fadb14' : '#adc6ff'}`, 
                                                    paddingLeft: '20px' 
                                                }}>
                                                    <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '8px' }}>
                                                        Délégués assignés au portefeuille :
                                                    </Text>
                                                    <Space wrap size={[4, 8]}>
                                                        {firstRecord.assignedDelegates?.length > 0 ? (
                                                            firstRecord.assignedDelegates.map((name, idx) => (
                                                                <Tag key={idx} color="cyan" icon={<RocketOutlined />} style={{ borderRadius: '10px' }}>
                                                                    {name.toUpperCase()}
                                                                </Tag>
                                                            ))
                                                        ) : (
                                                            <Text type="secondary" italic style={{ fontSize: '11px' }}>Aucun délégué assigné</Text>
                                                        )}
                                                    </Space>
                                                </Col>
                                            </Row>
                                        </div>

                                        {/* ⚡ 2. ACCORDÉON DES OUBLIS DE PLANIFICATION PAR CYCLE (>= 2026) ⚡ */}
                                        {cyclesCoverage.length > 0 && (
                                            <div style={{ marginBottom: 16 }}>
                                                <Collapse
                                                    defaultActiveKey={delegatesWithUnassigned.length > 0 ? ['audit_oubli'] : []}
                                                    style={{ 
                                                        background: delegatesWithUnassigned.length > 0 ? '#fff2f0' : '#f6ffed', 
                                                        border: delegatesWithUnassigned.length > 0 ? '1px solid #ffa39e' : '1px solid #b7eb8f',
                                                        borderRadius: '8px'
                                                    }}
                                                    items={[
                                                        {
                                                            key: 'audit_oubli',
                                                            label: (
                                                                <Space>
                                                                    {delegatesWithUnassigned.length > 0 ? (
                                                                        <>
                                                                            <WarningOutlined style={{ color: '#cf1322', fontSize: '16px' }} />
                                                                            <span style={{ color: '#cf1322', fontWeight: 'bold', fontSize: '13px' }}>
                                                                                Alertes Oublis de Planification par Cycle
                                                                            </span>
                                                                            <Badge count={`${totalOublisCount} cycle(s) oublié(s)`} style={{ backgroundColor: '#cf1322' }} />
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                                                                            <span style={{ color: '#389e0d', fontWeight: 'bold', fontSize: '13px' }}>
                                                                                Planification Optimale : Ce médecin est planifié dans 100% des cycles
                                                                            </span>
                                                                            <Tag color="success" style={{ marginLeft: 8 }}>100% Couvert</Tag>
                                                                        </>
                                                                    )}
                                                                </Space>
                                                            ),
                                                            children: (
                                                                <div>
                                                                    {delegatesWithUnassigned.length > 0 ? (
                                                                        delegatesWithUnassigned.map((cov, idx) => {
                                                                            const unassignedList = cov.unassignedCycles || [];
                                                                            const assignedList = cov.assignedCycles || [];

                                                                            return (
                                                                                <div 
                                                                                    key={idx} 
                                                                                    style={{ 
                                                                                        padding: '10px 14px', 
                                                                                        background: '#ffffff', 
                                                                                        borderRadius: '6px', 
                                                                                        border: '1px solid #ffccc7',
                                                                                        marginBottom: idx !== delegatesWithUnassigned.length - 1 ? 8 : 0 
                                                                                    }}
                                                                                >
                                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                                                                        <Text strong style={{ color: '#1890ff', fontSize: '13px' }}>
                                                                                            👤 Délégué : {cov.delegateName}
                                                                                        </Text>
                                                                                        <Tag color="default" style={{ fontSize: '11px', color: '#595959' }}>
                                                                                            Planifié dans {assignedList.length} autre(s) cycle(s)
                                                                                        </Tag>
                                                                                    </div>

                                                                                    <div>
                                                                                        <Text strong style={{ fontSize: '12px', color: '#cf1322', display: 'block', marginBottom: 6 }}>
                                                                                            ⚠️ NON ASSIGNÉ dans ces cycles ({unassignedList.length}) :
                                                                                        </Text>
                                                                                        <Space wrap size={[6, 8]}>
                                                                                            {unassignedList.map((c, cIdx) => {
                                                                                                const periodStr = c.startDate && c.endDate 
                                                                                                    ? `(${dayjs(c.startDate).format('DD/MM/YY')} au ${dayjs(c.endDate).format('DD/MM/YY')})`
                                                                                                    : '';

                                                                                                return (
                                                                                                    <Tag 
                                                                                                        key={cIdx} 
                                                                                                        color="error" 
                                                                                                        style={{ 
                                                                                                            fontWeight: 'bold', 
                                                                                                            fontSize: '11px', 
                                                                                                            padding: '3px 10px',
                                                                                                            borderRadius: '4px' 
                                                                                                        }}
                                                                                                    >
                                                                                                        ⚠️ {c.cycleName} <span style={{ fontWeight: 'normal', opacity: 0.9, marginLeft: 4 }}>{periodStr}</span>
                                                                                                    </Tag>
                                                                                                );
                                                                                            })}
                                                                                        </Space>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })
                                                                    ) : (
                                                                        <Text style={{ color: '#389e0d', fontSize: '12px' }}>
                                                                            Tous les délégués assignés ont bien planifié ce médecin dans l'ensemble de leurs cycles. Aucun oubli détecté.
                                                                        </Text>
                                                                    )}
                                                                </div>
                                                            )
                                                        }
                                                    ]}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })()
                        )}
                        
                        {/* 2. EN-TÊTE DU TABLEAU DES VISITES (AVEC NOUVELLE COLONNE OBSERVATIONS) */}
                        <div style={{ 
                            background: '#f5f5f5', 
                            padding: '10px 15px', 
                            borderRadius: '6px', 
                            marginBottom: '10px', 
                            display: 'flex', 
                            fontWeight: 'bold',
                            fontSize: '12px',
                            border: '1px solid #e8e8e8'
                        }}>
                            <div style={{ width: '15%' }}>Date & Heure</div>
                            <div style={{ width: '20%' }}>Visiteur</div>
                            <div style={{ width: '22%' }}>Échantillons distribués</div>
                            <div style={{ width: '18%' }}>Produits présentés</div>
                            <div style={{ width: '25%' }}>Observations / Notes</div>
                        </div>

                        {/* 3. LISTE DES VISITES AVEC IDENTIFICATION SUPERVISEUR */}
                        <List 
                            dataSource={doctorDetailData} 
                            locale={{ emptyText: "Aucune visite trouvée pour ce médecin." }}
                            renderItem={item => {
                                const isSupervisorVisit = item.isSupervisor || 
                                    (item.note && (item.note.includes('[RAPPORT SUPERVISEUR') || item.note.includes('[VISITE DE CONTRÔLE') || item.note.includes('[SUPERVISION'))) ||
                                    (item.name && (item.name.includes('[SUPERVISEUR]') || item.name.toLowerCase().includes('visite de contrôle')));

                                let displayNote = item.note || item.comment || '';
                                if (displayNote.includes('Observations :')) {
                                    displayNote = displayNote.split('Observations :')[1].trim();
                                }

                                return (
                                    <List.Item style={{ 
                                        padding: '12px 15px', 
                                        marginBottom: '8px',
                                        borderRadius: '6px',
                                        // ⚡ FOND VIOLET DOUX ET BORDURE VIOLETTE POUR LES SUPERVISEURS ⚡
                                        background: isSupervisorVisit ? '#fcf6ff' : '#ffffff', 
                                        border: isSupervisorVisit ? '1px solid #d3adf7' : '1px solid #f0f0f0',
                                        borderLeft: isSupervisorVisit ? '5px solid #722ed1' : '1px solid #f0f0f0',
                                        transition: 'all 0.2s'
                                    }}>
                                        <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                                            {/* Date & Heure */}
                                            <div style={{ width: '15%' }}>
                                                <Text strong>{dayjs(item.reportDate).format('DD/MM/YYYY')}</Text>
                                                <br/>
                                                <Text type="secondary" style={{ fontSize: '11px' }}>{dayjs(item.reportDate).format('HH:mm')}</Text>
                                            </div>

                                            {/* Visiteur (Badge violet avec couronne 👑 si superviseur) */}
                                            <div style={{ width: '20%' }}>
                                                {isSupervisorVisit ? (
                                                    <Tag color="purple" style={{ fontWeight: 'bold', padding: '3px 8px', borderRadius: '4px' }}>
                                                        <CrownOutlined style={{ marginRight: 5 }} /> {item.delegateName}
                                                    </Tag>
                                                ) : (
                                                    <Tag icon={<UserOutlined />} color="default">
                                                        {item.delegateName}
                                                    </Tag>
                                                )}
                                            </div>

                                            {/* Échantillons */}
                                            <div style={{ width: '22%' }}> 
                                                {item.samples && item.samples.length > 0 ? (
                                                    item.samples.map((s, i) => (
                                                        <Tag 
                                                            key={i} 
                                                            color={s.isSpecific ? "error" : "processing"} 
                                                            style={{ marginBottom: '3px', fontSize: '11px' }}
                                                        >
                                                            {s.isSpecific && "[HORS CIBLE] "}{s.name} x{s.quantity}
                                                        </Tag>
                                                    ))
                                                ) : (
                                                    <Text type="secondary" style={{ fontSize: '11px' }}>Aucun</Text>
                                                )}
                                            </div>

                                            {/* Produits */}
                                            <div style={{ width: '18%' }}> 
                                                {item.products && item.products.length > 0 ? (
                                                    item.products.map((p, i) => (
                                                        <Tag key={i} color="orange" style={{ marginBottom: '3px', fontSize: '11px' }}>{p}</Tag>
                                                    ))
                                                ) : (
                                                    <Text type="secondary" style={{ fontSize: '11px' }}>-</Text>
                                                )}
                                            </div>

                                            {/* ⚡ NOUVELLE COLONNE : OBSERVATIONS / NOTES ⚡ */}
                                            <div style={{ width: '25%' }}>
                                                {displayNote ? (
                                                    <Tooltip title={item.note || displayNote}>
                                                        <div style={{ 
                                                            fontSize: '12px', 
                                                            color: isSupervisorVisit ? '#531dab' : '#262626', 
                                                            background: isSupervisorVisit ? '#f9f0ff' : '#f5f5f5',
                                                            padding: '5px 10px',
                                                            borderRadius: '4px',
                                                            border: isSupervisorVisit ? '1px dashed #d3adf7' : '1px solid #e8e8e8',
                                                            maxHeight: '48px',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            cursor: 'pointer'
                                                        }}>
                                                            📝 {displayNote}
                                                        </div>
                                                    </Tooltip>
                                                ) : (
                                                    <Text type="secondary" style={{ fontSize: '11px' }}>-</Text>
                                                )}
                                            </div>
                                        </div>
                                    </List.Item>
                                );
                            }} 
                        />
                        {doctorDetailData.length === 0 && !doctorLoading && <Empty description="Aucune visite trouvée pour ce médecin." />}
                    </Spin>
                </Modal>
            </Card>

            <style>{`
                .row-partner-gold > td { background-color: #fffbe6 !important; }
                .row-partner-gold > td.ant-table-cell-fix-left { background-color: #fffbe6 !important; }
                .row-partner-gold:hover > td { background-color: #fff1b8 !important; }
                .row-partner-gold a { color: #856404 !important; }
            `}</style>
        </div>
    );
};

export default WeeklyMissedVisitsPage;